import { useCallback } from "react";
import { useParams } from "react-router-dom";
import type { ToolkitAction } from "src/state/toolkitReduxStore";
import { NOOP } from "@web-monorepo/fetchers";
import GroupsRemote from "./GroupsRemote";
import { displayOpen, displayClose, remoteClose } from "src/pods/realtime";
import { useAttendingStudents } from "src/pods/students/useStudentsFetcher";
import {
  useGroupRestrictionsFetcher,
  useEditGroupRestrictionsMutation,
} from "src/pods/groups/useGroupRestrictionsFetcher";
import { dispatchToolkitReduxAction } from "src/state/toolkitReduxStore";
import type { GroupRestriction } from "src/pods/groups/createGroups";

export default function GroupsRemoteContainer() {
  const { classId, mountPoint } = useParams<{ classId: string; mountPoint: string }>();
  const { data: students } = useAttendingStudents(classId ?? "", { enabled: !!classId });
  const { data: restrictionsData } = useGroupRestrictionsFetcher(
    classId ? { metaDataName: `toolkitGroupRestrictions_${classId}` } : NOOP,
  );
  const { mutate: editRestrictions } = useEditGroupRestrictionsMutation();

  const groupRestrictions = (restrictionsData?.value ?? []) as GroupRestriction[];

  const handleDisplayOpen = useCallback(
    (url: string, action?: ToolkitAction) => dispatchToolkitReduxAction(displayOpen(url, action)),
    [],
  );
  const handleDisplayClose = useCallback(() => dispatchToolkitReduxAction(displayClose()), []);
  const handleRemoteClose = useCallback(() => dispatchToolkitReduxAction(remoteClose()), []);

  if (!classId || !mountPoint) return null;

  return (
    <GroupsRemote
      classId={classId}
      mountPoint={mountPoint}
      students={students}
      groupRestrictions={groupRestrictions}
      displayOpen={handleDisplayOpen}
      displayClose={handleDisplayClose}
      remoteClose={handleRemoteClose}
      editGroupRestrictions={({
        classId,
        groupOfStudentIdGroups,
      }: {
        classId: string;
        groupOfStudentIdGroups: GroupRestriction[];
      }) => {
        editRestrictions({
          path: { metaDataName: `toolkitGroupRestrictions_${classId}` },
          body: { value: groupOfStudentIdGroups },
        });
      }}
    />
  );
}
